import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import { Box, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslate } from 'ra-core'
import { useRecordContext } from 'react-admin'

import MarkerField from './field'
import IncidentReportShowCard from './IncidentReportShowCard'
import { SUMMARY_I18N_PREFIX } from './showShared'
import AdvancedTextField from '../../components/AdvancedTextField'
import MediaStackViewer from '../../components/MediaStackViewer'

const EvidenceNoteSection = () => {
  const translate = useTranslate()

  return (
    <Box
      sx={{
        px: { xs: 2, md: 2.25 },
        pb: { xs: 2, md: 2.25 },
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'text.secondary',
          mb: 0.75,
        }}
      >
        {translate('resources.incident-reports.fields.note')}
      </Typography>
      <Box sx={{ color: 'text.primary', lineHeight: 1.55 }}>
        <AdvancedTextField
          source="note"
          fallback="resources.incident-reports.show.note.fallback"
          multiline
          label={false}
        />
      </Box>
    </Box>
  )
}

const IncidentReportEvidenceCard = () => {
  const record = useRecordContext()
  const translate = useTranslate()
  const hasPicture = Boolean(record.picture)
  const hasBlueprint = Boolean(record.blueprint_id)

  const mediaItems = [
    hasPicture
      ? {
          id: 'picture',
          label: translate(`${SUMMARY_I18N_PREFIX}.media.picture`),
          renderMain: () => (
            <Box
              component="img"
              src={record.picture}
              alt={translate(`${SUMMARY_I18N_PREFIX}.evidence.title`)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ),
          renderPreview: () => (
            <Box
              component="img"
              src={record.picture}
              alt={translate(`${SUMMARY_I18N_PREFIX}.media.picture`)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ),
          renderThumbnail: () => (
            <Box
              component="img"
              src={record.picture}
              alt={translate(`${SUMMARY_I18N_PREFIX}.media.picture`)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ),
        }
      : null,
    hasBlueprint
      ? {
          id: 'blueprint',
          label: translate(`${SUMMARY_I18N_PREFIX}.media.blueprint`),
          renderMain: () => <MarkerField containerWidth={320} markerSize={24} />,
          previewSurfaceSx: {
            borderRadius: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.96) : theme.palette.common.white,
          },
          renderPreview: () => <MarkerField containerWidth={960} markerSize={28} />,
          renderThumbnail: () => (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MarkerField containerWidth={84} markerSize={14} />
            </Box>
          ),
        }
      : null,
  ].filter(Boolean)

  return (
    <IncidentReportShowCard titleSuffix="evidence.title" icon={ImageOutlinedIcon}>
      {hasPicture || hasBlueprint ? (
        <Box
          sx={{
            flex: '0 0 auto',
            px: { xs: 2, md: 2.25 },
            py: { xs: 2, md: 2.25 },
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.46)} 0%, ${alpha(
                    theme.palette.background.paper,
                    0.72,
                  )} 100%)`
                : `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.92)} 0%, ${alpha(
                    theme.palette.background.paper,
                    0.96,
                  )} 100%)`,
          }}
        >
          <MediaStackViewer items={mediaItems} desktopHeight={300} empty={null} />
        </Box>
      ) : null}
      <EvidenceNoteSection />
    </IncidentReportShowCard>
  )
}

export default IncidentReportEvidenceCard
